import { Link } from "react-router-dom";
import { useState } from "react";
import type { PowerballPredictionFeed } from "../../powerball/usePowerballPredictionFeed.ts";
import "../../styles/powerball-workspace.css";

export const POWERBALL_TIERS = [
  {
    tab: "p10",
    tier: "powerball_10",
    title: "PB10",
    mark: "P10",
    description: "A focused match-result board across qualified leagues.",
  },
  {
    tab: "p20",
    tier: "mega_powerball_20",
    title: "Mega 25",
    mark: "M25",
    description: "Broader match-result coverage across the daily slate.",
  },
  {
    tab: "mm",
    tier: "mixed_mega_jackpot",
    title: "Mixed Mega",
    mark: "MX",
    description: "A broad board of supported fixtures and markets.",
  },
  {
    tab: "perfect10",
    tier: "perfect10",
    title: "Perfect 10",
    mark: "10",
    description: "A focused shortlist drawn from the POWERBALL tiers.",
  },
  {
    tab: "lower",
    tier: "lower_lines",
    title: "Lower Lines",
    mark: "LL",
    description: "Lower-priced selections. Low odds still carry risk.",
  },
  {
    tab: "goals",
    tier: "goals_bank",
    title: "Goals Bank",
    mark: "GB",
    description: "Dedicated match goals, team goals and scoring selections.",
  },
  {
    tab: "big",
    tier: "big_stage",
    title: "The Big Stage",
    mark: "BS",
    description: "The UEFA competition-week board.",
  },
  {
    tab: "multiverse",
    tier: "multiverse",
    title: "MULTIVERSE",
    mark: "MV",
    description: "One supported market per fixture across a wider market range.",
  },
] as const;

export function PowerballWorkspaceNav({ active }: { active: "discover" | "boards" | "history" }) {
  return (
    <nav className="pb-workspace-nav" aria-label="POWERBALL workspace">
      <Link aria-current={active === "discover" ? "page" : undefined} to="/powerball">
        Discover
      </Link>
      <Link aria-current={active === "boards" ? "page" : undefined} to="/powerball?tab=p10">
        Prediction boards
      </Link>
      <Link aria-current={active === "history" ? "page" : undefined} to="/powerball/history">
        History &amp; settlement
      </Link>
      <span>POWERBALL / FOOTBALL</span>
    </nav>
  );
}

export function PowerballDiscover({
  feed,
  error = false,
  onOpen,
}: {
  feed: PowerballPredictionFeed | null;
  error?: boolean;
  onOpen: (tab: string) => void;
}) {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState<string>("p10");
  const available = POWERBALL_TIERS.filter(
    (t) => (feed?.tiers[t.tier]?.selections.length ?? 0) > 0,
  ).length;
  const picked = POWERBALL_TIERS.find((t) => t.tab === selected) ?? POWERBALL_TIERS[0];
  const board = feed?.tiers[picked.tier];
  const rows = POWERBALL_TIERS.filter(
    (t) =>
      filter === "all" ||
      (filter === "available"
        ? !!feed?.tiers[t.tier]?.selections.length
        : feed?.tiers[t.tier]?.selections.length === 0),
  );
  return (
    <>
      <div className="pb-discover-toolbar">
        <div className="pb-segmented" aria-label="Filter tiers">
          {[
            ["all", "All tiers", 8],
            ["available", "With selections", feed ? available : "—"],
            ["empty", "No selections", feed ? 8 - available : "—"],
          ].map(([key, label, count]) => (
            <button key={key} aria-pressed={filter === key} onClick={() => setFilter(String(key))}>
              {label} <span>{count}</span>
            </button>
          ))}
        </div>
        <span className="pb-subtle">{feed?.session_date ?? "Current session"} · Accra</span>
      </div>
      {error && (
        <p role="alert" className="pb-notice">
          The latest prediction feed is unavailable. Refresh to try again.
        </p>
      )}
      <div className="pb-discover-layout">
        <section className="pb-tier-directory" aria-label="POWERBALL tiers">
          <div className="pb-directory-columns">
            <span>Tier / focus</span>
            <span>Current selections</span>
            <span>Review status</span>
          </div>
          {rows.map((t) => {
            const data = feed?.tiers[t.tier];
            const count = data?.selections.length;
            const leagues = new Set(data?.selections.map((s) => s.league)).size;
            return (
              <button
                className={`pb-directory-row${selected === t.tab ? " is-selected" : ""}`}
                key={t.tab}
                onClick={() => setSelected(t.tab)}
                aria-pressed={selected === t.tab}
              >
                <span className="pb-tier-identity">
                  <span className="pb-tier-mark">{t.mark}</span>
                  <span>
                    <strong>{t.title}</strong>
                    <small>{t.description}</small>
                  </span>
                </span>
                <span className="pb-directory-count">
                  <strong>
                    {count === undefined
                      ? "—"
                      : `${count} ${count === 1 ? "selection" : "selections"}`}
                  </strong>
                  <small>
                    {count
                      ? `${leagues} ${leagues === 1 ? "league" : "leagues"}`
                      : data
                        ? "No current selections"
                        : error
                          ? "Unavailable"
                          : "Loading…"}
                  </small>
                </span>
                <span className={`pb-state-tag${count ? " pending" : ""}`}>
                  {count
                    ? "Predictions"
                    : data
                      ? "No selections"
                      : error
                        ? "Unavailable"
                        : "Loading"}
                </span>
              </button>
            );
          })}
          {rows.length === 0 && <p className="pb-directory-empty">No tiers match this filter.</p>}
        </section>
        <aside className="pb-discover-aside">
          <section className="pb-tier-preview">
            <span className="pb-eyebrow">EXPLORE A BOARD</span>
            <h2>{picked.title}</h2>
            <p>{picked.description}</p>
            <div className="pb-preview-number">
              {board?.selections.length ?? "—"}
              <span>current selections</span>
            </div>
            <dl>
              <div>
                <dt>Session</dt>
                <dd>{feed?.session_date ?? "Loading"}</dd>
              </div>
              <div>
                <dt>Ticket review</dt>
                <dd>{board?.selections.length ? "Pending" : "No current ticket"}</dd>
              </div>
            </dl>
            <button className="pb-primary-button" onClick={() => onOpen(picked.tab)}>
              View {picked.title} board <span aria-hidden="true">↗</span>
            </button>
            <small>Predictions stay visible while ticket review is pending.</small>
          </section>
          <section className="pb-workspace-note">
            <h3>A clearer view of every selection.</h3>
            <p>
              Explore the live boards, inspect the exact market and price, then follow saved
              publications through to settlement.
            </p>
            <Link to="/powerball/history">Browse publication history →</Link>
          </section>
        </aside>
      </div>
    </>
  );
}
